import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, XCircle, AlertTriangle, Info, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Install Starter Pack (button + dialog) ─────────────────────────── */
export function StarterPackButton({ authHeaders, onInstalled }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);
  const [links, setLinks] = useState({ booking_url_candidate: "", booking_url_employer: "", booking_url_relocator: "" });

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/email-hub/starter-pack/status`, { headers: authHeaders });
      setStatus(r.data);
      setLinks({
        booking_url_candidate: r.data.settings?.booking_url_candidate || "",
        booking_url_employer: r.data.settings?.booking_url_employer || "",
        booking_url_relocator: r.data.settings?.booking_url_relocator || "",
      });
    } catch (e) {
      toast.error(`Status load failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, [authHeaders]);

  useEffect(() => { if (open) loadStatus(); }, [open, loadStatus]);

  const install = async () => {
    setInstalling(true);
    try {
      const r = await axios.post(`${API}/admin/email-hub/starter-pack/install`, {}, { headers: authHeaders });
      const { templates, automations } = r.data;
      toast.success(
        `Installed ${templates.inserted} templates (${templates.existing} already there) · ` +
        `${automations.inserted} journeys (${automations.existing} already there).`
      );
      await loadStatus();
      onInstalled?.();
    } catch (e) {
      toast.error(`Install failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setInstalling(false); }
  };

  const saveLinks = async () => {
    setSavingLinks(true);
    try {
      await axios.patch(`${API}/admin/email-hub/settings`, links, { headers: authHeaders });
      toast.success("Booking links saved — rebook buttons across all starter emails now use them.");
      await loadStatus();
      onInstalled?.();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSavingLinks(false); }
  };

  const fullyInstalled = status &&
    status.templates.installed >= status.templates.total &&
    status.automations.installed >= status.automations.total;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        data-testid="starter-pack-open"
        className="bg-[#C6D21F] hover:bg-[#C6D21F]/90 text-[#0D2F4D] font-bold"
      >
        <Sparkles className="h-4 w-4 mr-1.5"/>Install Starter Pack
      </Button>

      {open && (
        <Dialog open={true} onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="starter-pack-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#0D2F4D]">
                <Sparkles className="h-5 w-5 text-[#C6D21F]"/>Starter Automation Pack
              </DialogTitle>
              <DialogDescription className="text-xs text-[#6F8FA8]">
                30 branded templates + 12 journeys (Booking, Cancellation, No-Show, Post-Call) for
                Candidates, Employers and Relocators. Journeys install as <strong>inactive drafts</strong> —
                nothing sends until you activate them. Re-installing never overwrites your edits.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {status && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#E4E7EA] bg-[#FAFAF7] p-3 text-center" data-testid="starter-pack-tpl-count">
                    <div className="text-2xl font-bold text-[#0D2F4D]">{status.templates.installed}<span className="text-sm text-[#6F8FA8]">/{status.templates.total}</span></div>
                    <div className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold">Templates installed</div>
                  </div>
                  <div className="rounded-xl border border-[#E4E7EA] bg-[#FAFAF7] p-3 text-center" data-testid="starter-pack-auto-count">
                    <div className="text-2xl font-bold text-[#0D2F4D]">{status.automations.installed}<span className="text-sm text-[#6F8FA8]">/{status.automations.total}</span></div>
                    <div className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold">Journeys installed</div>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-[#E4E7EA] p-3 space-y-3">
                <div>
                  <div className="text-xs font-bold text-[#0D2F4D]">Booking links (Calendly)</div>
                  <p className="text-[11px] text-[#6F8FA8]">Rebook buttons in cancellation and no-show emails point here — one link per pipeline, used everywhere.</p>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Candidate call (Careers)</label>
                  <Input value={links.booking_url_candidate}
                    onChange={(e) => setLinks({ ...links, booking_url_candidate: e.target.value })}
                    placeholder="https://calendly.com/…/careers-call-candidate" data-testid="starter-pack-link-candidate"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Employer call (Careers)</label>
                  <Input value={links.booking_url_employer}
                    onChange={(e) => setLinks({ ...links, booking_url_employer: e.target.value })}
                    placeholder="https://calendly.com/…/careers-call-employer" data-testid="starter-pack-link-employer"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Relocator call (Collective)</label>
                  <Input value={links.booking_url_relocator}
                    onChange={(e) => setLinks({ ...links, booking_url_relocator: e.target.value })}
                    placeholder="https://calendly.com/…/30min" data-testid="starter-pack-link-relocator"/>
                </div>
                <Button size="sm" variant="outline" onClick={saveLinks} disabled={savingLinks} data-testid="starter-pack-save-links">
                  {savingLinks ? "Saving…" : "Save booking links"}
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}><X className="h-4 w-4 mr-1"/>Close</Button>
              <Button onClick={install} disabled={installing}
                className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" data-testid="starter-pack-install">
                {installing ? "Installing…" : fullyInstalled ? "Re-check / repair pack" : "Install Starter Pack"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

/* ── Setup checklist badge (per automation row) ─────────────────────── */
export function SetupBadge({ checklist, onClick, id = "" }) {
  const sfx = id ? `-${id}` : "";
  if (!checklist) return <Badge variant="outline" className="text-[10px]">—</Badge>;
  if (checklist.blockers > 0) {
    return (
      <button onClick={onClick} data-testid={`setup-badge-blocked${sfx}`} className="inline-flex">
        <Badge className="bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer">
          <XCircle className="h-3 w-3 mr-1"/>{checklist.blockers} to fix
        </Badge>
      </button>
    );
  }
  if (checklist.warnings > 0) {
    return (
      <button onClick={onClick} data-testid={`setup-badge-warn${sfx}`} className="inline-flex">
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer">
          <AlertTriangle className="h-3 w-3 mr-1"/>Ready · {checklist.warnings} note{checklist.warnings === 1 ? "" : "s"}
        </Badge>
      </button>
    );
  }
  return (
    <button onClick={onClick} data-testid={`setup-badge-ready${sfx}`} className="inline-flex">
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 cursor-pointer">
        <CheckCircle2 className="h-3 w-3 mr-1"/>Ready
      </Badge>
    </button>
  );
}

const ITEM_ICON = {
  pass: <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5"/>,
  fail: <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5"/>,
  warn: <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5"/>,
  info: <Info className="h-4 w-4 text-[#6F8FA8] shrink-0 mt-0.5"/>,
};

/* ── Setup checklist dialog ─────────────────────────────────────────── */
export function ChecklistDialog({ automation, checklist, onClose }) {
  const items = checklist?.items || [];
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="setup-checklist-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D2F4D]">Setup checklist — {automation.name}</DialogTitle>
          <DialogDescription className="text-xs text-[#6F8FA8]">
            {checklist?.blockers > 0
              ? `${checklist.blockers} item${checklist.blockers === 1 ? "" : "s"} must be fixed before this journey can go live.`
              : "All blockers clear — this journey is safe to activate."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {items.length === 0 && <p className="text-sm text-[#6F8FA8]">No checks available — save the automation first.</p>}
          {items.map((it) => (
            <div key={it.id} className="flex items-start gap-2.5 rounded-xl border border-[#E4E7EA] bg-[#FAFAF7] px-3 py-2.5"
              data-testid={`checklist-item-${it.id}`}>
              {ITEM_ICON[it.status] || ITEM_ICON.info}
              <div className="min-w-0">
                <div className={`text-sm font-semibold ${it.status === "fail" ? "text-red-800" : it.status === "warn" ? "text-amber-800" : "text-[#0D2F4D]"}`}>{it.label}</div>
                {it.hint && <div className="text-xs text-[#6F8FA8] mt-0.5">{it.hint}</div>}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="setup-checklist-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
